import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FileText,
  House,
  GitBranch,
  Users,
  ClockCounterClockwise,
  Gear,
  SignOut,
  List,
  XCircle,
  EnvelopeSimple,
  Question,
  PaperPlaneTilt,
  Buildings,
  Bell,
  AddressBook,
  Globe,
  UploadSimple,
  Trash
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children, user, onLogout, selectedProject, setSelectedProject, projects }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('sidebarOpen');
    return stored === null ? false : stored === 'true';
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  
  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';
  const permissions = user?.permissions || {};

  // Build navigation based on user permissions
  const navigation = [
    { name: 'Dashboard', path: '/', icon: House, show: true },
    { name: 'Transmittals', path: '/transmittals', icon: PaperPlaneTilt, show: permissions.can_view_transmittals || isSuperAdmin },
    { name: 'Documents', path: '/documents', icon: FileText, show: permissions.can_view_all_documents || isSuperAdmin },
    { name: 'Bulk Upload', path: '/bulk-upload', icon: UploadSimple, show: permissions.can_upload_documents || isSuperAdmin },
    { name: 'MDR', path: '/mdr', icon: AddressBook, show: permissions.can_view_mdr || isSuperAdmin },
    { name: 'Correspondence', path: '/correspondence', icon: EnvelopeSimple, show: permissions.can_view_correspondence || isSuperAdmin },
    { name: 'Queries', path: '/queries', icon: Question, show: permissions.can_view_queries || isSuperAdmin },
    { name: 'DDM Settings', path: '/ddm', icon: AddressBook, show: permissions.can_manage_ddm || isSuperAdmin },
    { name: 'Workflows', path: '/workflows', icon: GitBranch, show: permissions.can_approve_reviews || isSuperAdmin },
    { name: 'Users', path: '/users', icon: Users, show: permissions.can_manage_users || isSuperAdmin },
    { name: 'Projects', path: '/projects', icon: Buildings, show: isSuperAdmin },
    { name: 'Activity', path: '/activity', icon: ClockCounterClockwise, show: isSuperAdmin },
    { name: 'Trash', path: '/trash', icon: Trash, show: isSuperAdmin },
    { name: 'Settings', path: '/settings', icon: Gear, show: isSuperAdmin },
  ].filter(item => item.show);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenNotifications = () => {
    setNotifOpen(true);
    fetchNotifications();
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Backdrop (only when sidebar is open) - click to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity"
          onClick={() => {
            setSidebarOpen(false);
            localStorage.setItem('sidebarOpen', 'false');
          }}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar - Apple Style */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-[260px] flex flex-col bg-white/90 backdrop-blur-xl border-r border-gray-200/50 shadow-xl transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-900 rounded-lg flex items-center justify-center">
              <FileText size={18} weight="bold" className="text-white" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">DocStat</h1>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.name}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all ${
                  active
                    ? 'bg-blue-900 text-white font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon size={18} weight={active ? 'bold' : 'regular'} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-800 to-blue-900 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-500 truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            size="sm"
            className="w-full rounded-lg border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            data-testid="logout-button"
          >
            <SignOut size={16} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header - Apple Style */}
        <header className="sticky top-0 z-40 h-14 flex items-center justify-between px-6 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setSidebarOpen((prev) => {
                const next = !prev;
                localStorage.setItem('sidebarOpen', String(next));
                return next;
              });
            }}
            className="h-9 w-9 rounded-lg hover:bg-gray-100"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <XCircle size={20} className="text-gray-500" /> : <List size={20} className="text-gray-500" />}
          </Button>

          <div className="flex items-center gap-3">
            {/* Project Selector */}
            {projects && projects.length > 0 && (
              <div className="flex items-center gap-2">
                <Buildings size={16} className="text-gray-400" />
                <select
                  value={selectedProject || ''}
                  onChange={(e) => setSelectedProject && setSelectedProject(e.target.value)}
                  className="h-8 px-2 pr-6 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                  style={{ minWidth: '140px' }}
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* DocStat Portal Link */}
            <a
              href="/public"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="portal-link"
            >
              <Globe size={16} />
              <span className="hidden sm:inline">Portal</span>
            </a>
            
            {/* Notification Bell */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative h-9 w-9 rounded-lg hover:bg-gray-100"
                  onClick={handleOpenNotifications}
                  data-testid="notification-bell"
                >
                  <Bell size={18} className="text-gray-500" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 rounded-xl shadow-apple-lg border-gray-200" align="end">
                <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-gray-900">Notifications</h4>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs h-7 text-primary hover:text-primary-600" onClick={markAllRead}>
                      Mark all read
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No notifications</p>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id} 
                        className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                        onClick={() => {
                          markAsRead(notif.id);
                          // Prefer the explicit link if backend provided one (deep-links to cycle PDF, etc.)
                          if (notif.link) {
                            window.location.href = notif.link;
                          } else if (notif.resource_type === 'cycle' && notif.resource_id) {
                            window.location.href = `/documents?cycle=${notif.resource_id}`;
                          } else if (notif.resource_type === 'document' && notif.resource_id) {
                            window.location.href = `/documents/${notif.resource_id}`;
                          } else if (notif.resource_type === 'revision' && notif.resource_id) {
                            window.location.href = `/documents`;
                          }
                        }}
                      >
                        <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.created_at ? (String(notif.created_at).endsWith('Z') || String(notif.created_at).includes('+') ? notif.created_at : notif.created_at + 'Z') : 0).toLocaleString()}
                        </p>
                        {(notif.link || notif.resource_type === 'cycle') && (
                          <p className="text-xs text-blue-600 mt-1 hover:underline">Open review page →</p>
                        )}
                        {notif.resource_type === 'document' && notif.resource_id && !notif.link && (
                          <p className="text-xs text-blue-600 mt-1 hover:underline">Click to view document →</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            {user?.department && (
              <span className="text-sm text-gray-500 hidden md:inline">
                {user.department}
              </span>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
